import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "./auth-fetch";
import { api } from "./api";

/**
 * Email-verification hooks.
 *
 *   useSendEmailVerify() — POST /auth/email/verify/send (issues a magic
 *     link to the operator's email; banner copy says "we sent you a link").
 *   useConfirmEmailVerify() — POST /auth/email/verify/confirm { token }
 *     (called by the standalone verify-email landing page in the auth
 *     subapp once the operator clicks the link).
 */

export function useSendEmailVerify() {
  return useMutation({
    mutationFn: async () => {
      // openapi-fetch's typed client doesn't yet handle no-body POSTs in
      // a way Spring's @AuthenticationPrincipal-only endpoint expects;
      // bypass to authFetch for a clean 204.
      const res = await authFetch("/api/v1/auth/email/verify/send", {
        method: "POST",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { message?: string }).message ?? `HTTP ${res.status}`,
        );
      }
    },
  });
}

export function useConfirmEmailVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await api.POST(
        "/api/v1/auth/email/verify/confirm",
        { body: { token } },
      );
      if (error) {
        // Spring's default 4xx body is `{message: …}`. Surface that as
        // the Error.message so the page can map (`already_used` /
        // `expired` / `invalid_or_expired` / `wrong_purpose`) without
        // having to JSON.parse a stringified blob.
        const msg =
          (error as { message?: string }).message ?? "verify_failed";
        throw new Error(msg);
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user", "me"] });
    },
  });
}
